import { useMemo } from 'react'
import {
  DndContext,
  DragOverlay,
  closestCorners,
  useDroppable,
  type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { ScrollArea } from '@/components/ui/scroll-area'
import { IssueCard } from './issue-card'
import { useSetIssueField } from '@/lib/mutations'
import { byManualOrder, type ContextOrder } from '@/lib/context-order'
import { PRIORITY_ORDER } from '@/lib/stage'
import { PriorityDot } from '@/components/common/badges'
import type { WorkIssue } from '@/types/github'
import { dragIssue } from './drag-data'
import { SortableIssue } from './dnd-shared'
import { useDragHarness } from './use-drag-harness'

// Single vertical list in priority bands (P1 → Backlog), manually ordered within
// a band via this context's own order. Dragging
// within a band rewrites that band's order; dropping in another band changes
// Priority and slots the issue into the new band at its drop position.

function bandOf(issue: WorkIssue): string {
  return issue.priority ?? 'Backlog'
}

const BAND_PREFIX = 'band:'

/** Droppable wrapper for a whole band, so empty bands still accept drops. */
function BandDropZone({ band, children }: { band: string; children: React.ReactNode }) {
  const { setNodeRef } = useDroppable({ id: BAND_PREFIX + band })
  return <div ref={setNodeRef}>{children}</div>
}


export function PriorityList({
  issues,
  onOpen,
  order,
  onReorder,
  readOnly = false,
  excludeContext,
}: {
  issues: WorkIssue[]
  onOpen: (issue: WorkIssue) => void
  /** This context's manual order, keyed by priority band. */
  order: ContextOrder
  /** Persist a band's new id order (the hook also removes the issue from its old band). */
  onReorder: (band: string, orderedIds: string[]) => void
  /** Viewing someone else's list without reorder rights: cards open but don't drag. */
  readOnly?: boolean
  /** This list's own context, so a card doesn't flag itself. */
  excludeContext?: string
}) {
  const setField = useSetIssueField()
  const { active, setActive, sensors, openGuarded, markDragEnded } = useDragHarness(onOpen)

  const bands = useMemo(() => {
    const map = new Map<string, WorkIssue[]>()
    for (const p of PRIORITY_ORDER) map.set(p, [])
    for (const issue of issues) {
      const band = bandOf(issue)
      if (!map.has(band)) map.set(band, [])
      map.get(band)!.push(issue)
    }
    for (const [band, list] of map) list.sort(byManualOrder(order.groups[band] ?? []))
    return map
  }, [issues, order])

  const flat = useMemo(() => [...bands.values()].flat(), [bands])

  function handleDragEnd(event: DragEndEvent) {
    setActive(null)
    markDragEnded()
    if (readOnly) return
    const issue = dragIssue(event.active)
    const overId = event.over?.id
    if (!issue || overId === undefined || overId === issue.id) return

    const target = flat.filter((i) => i.id !== issue.id)

    let newBand: string
    let below: WorkIssue | null
    if (typeof overId === 'string' && overId.startsWith(BAND_PREFIX)) {
      // Dropped on an (often empty) band's open area → append to that band.
      newBand = overId.slice(BAND_PREFIX.length)
      below = null
    } else {
      const overIndex = target.findIndex((i) => i.id === overId)
      if (overIndex === -1) return
      const fromIndex = flat.findIndex((i) => i.id === issue.id)
      const movingDown = fromIndex !== -1 && fromIndex <= overIndex
      const insertAt = movingDown ? overIndex + 1 : overIndex
      const above = target[insertAt - 1] ?? null
      below = target[insertAt] ?? null
      // Between two bands, the drop reads as the top of the lower band.
      newBand = below ? bandOf(below) : above ? bandOf(above) : bandOf(issue)
    }

    // A cross-band drag is two independent writes (Priority field + order file),
    // not atomic: if the Priority write rolls back, the order file briefly lists
    // the issue in the new band. Harmless — a stale id is ignored on render
    // (byManualOrder) and pruned the next time that band is reordered.
    if (newBand !== bandOf(issue)) {
      setField.mutate({ issue, fieldName: 'Priority', value: newBand })
    }

    // Build the destination band's new id order with the dragged issue inserted
    // at its drop position, and persist it for this context.
    const bandBelow = below && bandOf(below) === newBand ? below : null
    const bandList = target.filter((i) => bandOf(i) === newBand).map((i) => i.id)
    const anchor = bandBelow ? bandList.indexOf(bandBelow.id) : bandList.length
    bandList.splice(anchor, 0, issue.id)

    onReorder(newBand, bandList)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e) => setActive(dragIssue(e.active) ?? null)}
      onDragCancel={() => setActive(null)}
      onDragEnd={handleDragEnd}
      accessibility={{
        screenReaderInstructions: {
          draggable:
            'Use arrow keys to move. Space to drop. Escape to cancel. Moving into another priority band changes the priority.',
        },
        announcements: {
          onDragStart: ({ active }) =>
            `Picked up #${dragIssue(active)?.number}.`,
          onDragOver: ({ active, over }) =>
            over
              ? `#${dragIssue(active)?.number} is over ${
                  typeof over.id === 'string' && over.id.startsWith(BAND_PREFIX)
                    ? `the ${over.id.slice(BAND_PREFIX.length)} band`
                    : 'another issue'
                }.`
              : `#${dragIssue(active)?.number} is over an empty area.`,
          onDragEnd: ({ active }) =>
            `Dropped #${dragIssue(active)?.number}.`,
          onDragCancel: ({ active }) =>
            `Cancelled dragging #${dragIssue(active)?.number}.`,
        },
      }}
    >
      <ScrollArea className="min-h-0 flex-1">
        <SortableContext items={flat.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <div className="mx-auto flex max-w-2xl flex-col gap-2 p-4">
            {PRIORITY_ORDER.map((priority) => {
              const list = bands.get(priority) ?? []
              return (
                <section key={priority} aria-label={priority}>
                  <h2 className="text-muted-foreground mb-1.5 flex items-center gap-1.5 px-1 pt-2 text-[11px] font-semibold tracking-wide uppercase">
                    <PriorityDot priority={priority} className="size-2" />
                    {priority}
                    <span className="text-faint-foreground font-mono font-normal">{list.length}</span>
                  </h2>
                  <BandDropZone band={priority}>
                    <div className="flex min-h-9 flex-col gap-2">
                      {list.map((issue) => (
                        <SortableIssue
                          key={issue.id}
                          issue={issue}
                          onOpen={openGuarded}
                          showEffort
                          disabled={readOnly}
                          excludeContext={excludeContext}
                        />
                      ))}
                      {list.length === 0 && !readOnly && (
                        <div className="border-border-strong text-faint-foreground rounded-lg border border-dashed p-3 text-center text-xs">
                          Drop here to set {priority}
                        </div>
                      )}
                    </div>
                  </BandDropZone>
                </section>
              )
            })}
          </div>
        </SortableContext>
      </ScrollArea>
      <DragOverlay>
        {active && (
          <div className="rotate-2 shadow-[var(--shadow-drag)]">
            <IssueCard issue={active} showEffort interactive={false} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}
